import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router'; 
import axios from 'axios';
import '../../App.css';
import ResearchSidebar from '../../Components/ResearchSidebar';
import { API_BASE_URL } from '../../utils/apiConfig';

// Hardcoded categories for the category area
const CATEGORIES = [
    { label: "Bakery" }, { label: "Dairy & Eggs" }, { label: "Produce" }, 
    { label: "Frozen" }, { label: "Meat & Seafood" }, { label: "Deli" }, 
    { label: "Baking Essentials" }, { label: "Snacks & Candy" }, { label: "Beverages" }, 
    { label: "Breakfast" }, { label: "Canned Goods & Soups" }, { label: "Condiments & Sauces" }, 
    { label: "Dry Goods & Pasta" }, { label: "Oils, Vinegars, & Spices" }, { label: "Prepared Foods" }, 
    { label: "Beer" }, { label: "Hard Beverages" }, { label: "Liquor" }, { label: "Wine" }
];

const Data = () => {
    const [orders, setOrders] = useState([]); // Raw session data from the database
    const [selectedSessions, setSelectedSessions] = useState([]); // IDs of checked sessions for bulk download
    const [loading, setLoading] = useState(true); // Toggle for loading spinner/text
    const [allExperiments, setAllExperiments] = useState([]); // List of all studies (Active and Finished)
    const [hiddenCategories, setHiddenCategories] = useState([]); // Track Labels to hide in new experiment
    const navigate = useNavigate();

    // all Factor Restrictions and the Restriction Style
    const [activeExperiment, setActiveExperiment] = useState({ 
        name: 'Nutritional Labels Study', 
        quota: 20,
        showSearch: true,
        showFilters: true,
        showCategories: true,
        showQuickInfo: true,      
        restrictionMode: 'HIDE'   
    }); 

    const [filterExpId, setFilterExpId] = useState('ALL'); // Dropdown filter state

    // Data Fetching Functions

    // Fetches the list of all experiment definitions (Names, Quotas, Active Status)
    const fetchExperiments = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/experiments`);
            setAllExperiments(res.data);
        } catch (err) {
            console.error("Failed to fetch experiments list", err);
        }
    }, []);

    // Fetches the actual participant session logs (Actions, Total Spent, etc.)
    const fetchAllOrders = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/all-orders`);
            setOrders(response.data);
        } catch (err) {
            console.error("Data Page: Failed to fetch order logs", err);
        }
    }, []);

    // Helper to run both fetches simultaneously and manage the loading state
    const refreshData = useCallback(async () => {
        setLoading(true);
        await Promise.all([fetchAllOrders(), fetchExperiments()]);
        setLoading(false);
    }, [fetchAllOrders, fetchExperiments]);

    // Initial load: Fetch data when the component mounts
    useEffect(() => {
        refreshData();
    }, [refreshData]);


    // Sends a request to create a NEW study record in the database
    const handleStartExperiment = async () => {
        // Generate the 8-digit join code automatically on click
        const newGeneratedCode = Math.floor(10000000 + Math.random() * 90000000).toString();

        try {
            const res = await axios.post(`${API_BASE_URL}/api/start-experiment`, { 
                conditionName: activeExperiment.name, 
                joinCode: newGeneratedCode, // Sending the code to the DB
                quota: activeExperiment.quota,
                showSearch: activeExperiment.showSearch,
                showFilters: activeExperiment.showFilters,
                showCategories: activeExperiment.showCategories,
                showQuickInfo: activeExperiment.showQuickInfo,
                restrictionMode: activeExperiment.restrictionMode,
                hiddenCategories: JSON.stringify(hiddenCategories)
            });
            alert(`Experiment Started! \n\nYour Qualtrics Join Code is: ${newGeneratedCode}`);
            refreshData(); // Refresh to show the newly created Study ID in the UI
        } catch (err) {
            console.error(err);
            alert("Failed to start experiment. Ensure your database has the joinCode column.");
        }
    };

    // Manually stops a live experiment by flipping the isActive flag to 0
    const handleStopExperiment = async (experimentId) => {
        if (!window.confirm("Are you sure you want to stop this experiment? New participants will no longer be able to join.")) {
            return;
        }

        try {
            await axios.put(`${API_BASE_URL}/api/stop-experiment/${experimentId}`);
            alert("Experiment stopped successfully.");
            refreshData(); // Refresh to flip the UI to 'RECENT'
        } catch (err) {
            console.error("Failed to stop experiment:", err);
            alert("Error stopping experiment. Please try again.");
        }
    };

    // Helper to toggle a category label in the hidden list (Checked = Enabled, Unchecked = Hidden)
    const toggleCategoryEnabled = (label) => {
        setHiddenCategories(prev => 
            prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
        );
    };

    // Data Transformation(the logic)

    // Organizes raw session rows into "Boxes" based on their Experiment ID
    const groupedData = useMemo(() => {
        const groups = { experiments: {}, general: [] };
        const experimentSessionIds = new Set();
        
        // 1. Group sessions belonging to specific Studies
        orders.forEach(order => {
            if (order.experimentId && order.experimentId !== 1) { 
                experimentSessionIds.add(order.sessionId);
                if (!groups.experiments[order.experimentId]) {
                    groups.experiments[order.experimentId] = {
                        name: order.experimentName || `Study ${order.experimentId}`,
                        sessions: []
                    };
                }
                groups.experiments[order.experimentId].sessions.push(order);
            }
        });

        // 2. Everything else goes into 'General Browsing'
        orders.forEach(order => {
            const isControl = !order.experimentId || order.experimentId === 1;
            if (isControl && !experimentSessionIds.has(order.sessionId)) {
                groups.general.push(order);
            }
        });

        return groups;
    }, [orders]);

    // Handles generating the CSV file and triggering the browser download
    const downloadCSV = (sessionList = null) => {
        const targetSessions = sessionList || orders.filter(order => selectedSessions.includes(order.sessionId));
        
        if (targetSessions.length === 0) {
            alert("Please select at least one session to download.");
            return;
        }

        const studyName = (sessionList && sessionList.length > 0 && sessionList[0].experimentName) 
            ? sessionList[0].experimentName.replace(/\s+/g, '_') 
            : 'General_Data';
        const dateStr = new Date().toISOString().split('T')[0];

        const headers = "Date,SessionID,ExperimentName,Status,Duration(min),TotalActions,NutritionViews,CartAdds,SearchUsage,TotalSpent,ItemsOrdered,SearchOn,FiltersOn,CategoriesOn,QuickInfoOn,RestrictionStyle\n";
        const csvContent = targetSessions.map(order => {
            const { date } = formatDateTime(order.sessionStart);
            const status = !!order.orderId ? 'Ordered' : 'Browsing';
            const duration = calculateDuration(order.sessionStart, order.sessionEnd);
            
            return [
                date,
                order.sessionId,
                `"${order.experimentName || 'Control'}"`,
                status,
                duration,
                order.interactionCount,
                order.nutritionViews || 0,
                order.cartAdds || 0,
                order.searchCount || 0,
                order.totalAmount ? Number(order.totalAmount).toFixed(2) : "0.00",
                `"${order.orderedItems || 'N/A'}"`,
                order.showSearch === 1 ? "YES" : "NO",
                order.showFilters === 1 ? "YES" : "NO",
                order.showCategories === 1 ? "YES" : "NO",
                order.showQuickInfo === 1 ? "YES" : "NO",
                order.restrictionMode || "HIDE"
            ].join(",");
        }).join("\n");

        const blob = new Blob([headers + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `UNL_${studyName}_${dateStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };


    // Determines which experiment to display in the top status bar (Live vs Recent)
    const bannerExp = useMemo(() => {
        if (!allExperiments || allExperiments.length === 0) return null;
        
        const active = allExperiments.find(exp => exp.isActive === 1);
        if (active) {
            return { ...active, status: active.currentParticipantCount >= active.participantQuota ? 'RECENT' : 'LIVE' };
        }

        const recent = [...allExperiments]
            .filter(exp => exp.experimentId !== 1) 
            .sort((a, b) => b.experimentId - a.experimentId)[0]; 
            
        return recent ? { ...recent, status: 'RECENT' } : null;
    }, [allExperiments]);

    // Helper Utils

    const handleDeleteSession = async (e, sessionId) => {
        e.stopPropagation(); 
        if (window.confirm(`Are you sure you want to delete session ${sessionId}?`)) {
            try {
                await axios.delete(`${API_BASE_URL}/api/session/${sessionId}`);
                setOrders(orders.filter(order => order.sessionId !== sessionId));
                setSelectedSessions(selectedSessions.filter(id => id !== sessionId)); 
            } catch (err) {
                console.error("Failed to delete session:", err);
                alert("Could not delete session. Please try again.");
            }
        }
    };

    const calculateDuration = (start, end) => {
        if (!start) return "0:00";
        const endTime = end ? new Date(end) : new Date(); // Use 'now' if session hasn't ended
        const durationMs = Math.max(0, endTime - new Date(start));
        const totalSeconds = Math.floor(durationMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return { date: "N/A", time: "N/A" };
        const dateObj = new Date(isoString);
        return {
            date: dateObj.toLocaleDateString(),
            time: dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
    };

    const handleToggleSelect = (e, sessionId) => {
        e.stopPropagation(); 
        setSelectedSessions(prev => 
            prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
        );
    };

    const renderSessionRow = (order) => {
        const { date, time } = formatDateTime(order.sessionStart);
        const isOrdered = !!order.orderId;
        const duration = calculateDuration(order.sessionStart, order.sessionEnd);
        const isSelected = selectedSessions.includes(order.sessionId);

        return (
            <div key={order.sessionId} className={`data-log-row ${isSelected ? 'selected' : ''}`}>
                <div className="row-main-content">
                    <input type="checkbox" checked={isSelected} onChange={(e) => handleToggleSelect(e, order.sessionId)} className="data-checkbox" />
                    <span className="data-timestamp"><strong>{date}</strong> <small style={{ color: '#888', marginLeft: '8px' }}>{time}</small></span>
                    <span className="session-id-tag">ID: {order.sessionId}</span>
                    <div className="quick-info-block">
                        <span><strong>Status:</strong> <span style={{ color: isOrdered ? '#056621' : '#cc8500' }}>{isOrdered ? 'Ordered' : 'Browsing'}</span></span>
                        <span className="dot">•</span>
                        <span><strong>Actions:</strong> {(order.interactionCount || 0)}</span>
                        <span className="dot">•</span>
                        <span><strong>Time:</strong> {duration} min</span>
                    </div>
                </div>
                <div className="row-actions">
                    <button className="delete-link" onClick={(e) => handleDeleteSession(e, order.sessionId)}>Delete</button>
                    <span className="details-link" onClick={() => navigate(`/research-portal/data/${order.sessionId}`)}>Details →</span>
                </div>
            </div>
        );
    };

    return (
        <main className="dashboard-layout">
            <ResearchSidebar />
            <div className="dashboard-content">
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 className="page-title">Participant Data</h1>
                    <button className="grey-subtle-btn" onClick={() => downloadCSV()}>Download Selected CSV ({selectedSessions.length})</button>
                </div>

                {/* Banner: Join Code Badge */}
                {bannerExp && (
                    <div className={`live-status-alert ${bannerExp.status === 'RECENT' ? 'recent-alert' : ''}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <span style={{ color: bannerExp.status === 'LIVE' ? '#3182ce' : '#888' }}>●</span>
                            <strong>{bannerExp.status} EXPERIMENT:</strong> {bannerExp.experimentName} 
                            <span style={{ margin: '0 5px', opacity: 0.3 }}>|</span>
                            
                            {/* THE FOLDER TAB DISPLAY FOR BANNER */}
                            <span className="join-code-badge">
                                <strong>JOIN CODE:</strong> <code style={{ color: '#e53e3e', fontWeight: '900' }}>{bannerExp.joinCode || 'N/A'}</code>
                            </span>

                            <span style={{ margin: '0 5px', opacity: 0.3 }}>|</span>
                            <strong>Joined:</strong> {bannerExp.currentParticipantCount} / {bannerExp.participantQuota}
                        </div>
            
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {bannerExp.status === 'LIVE' && (
                                <button className="stop-exp-btn" onClick={() => handleStopExperiment(bannerExp.experimentId)}>Stop Experiment</button>
                            )}
                            <button className="grey-refresh-btn" onClick={refreshData}>↻ Refresh Data</button>
                        </div>
                    </div>
                )}

                {/* Control Center: Code generated automatically on start */}
                <section className="experiment-controls-card">
                    <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Experiment Control Center</h3>
                    <div className="horizontal-controls-grid">
                        <div className="control-field" style={{ flex: 2 }}>
                            <label>Experiment Label:</label>
                            <input type="text" value={activeExperiment.name} onChange={(e) => setActiveExperiment({ ...activeExperiment, name: e.target.value })} placeholder="e.g. Nutritional Labels Study" />
                        </div>
                        <div className="control-field">
                            <label>Participant Quota:</label>
                            <input type="number" style={{ width: '80px' }} value={activeExperiment.quota} onChange={(e) => setActiveExperiment({ ...activeExperiment, quota: parseInt(e.target.value) || 0 })} />
                        </div>
                        <button className="grey-action-btn" onClick={handleStartExperiment} disabled={!activeExperiment.name || activeExperiment.quota <= 0}>
                            Start Live Experiment & Generate Code
                        </button>
                    </div>

                    <div className="experiment-config-section" style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '25px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        {['showSearch', 'showFilters', 'showCategories', 'showQuickInfo'].map(key => (
                            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                <input type="checkbox" checked={activeExperiment[key]} onChange={(e) => setActiveExperiment({...activeExperiment, [key]: e.target.checked})} />
                                {key.replace('show', 'Enable ')}
                            </label>
                        ))}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <label style={{ fontSize: '0.9rem' }}>Restriction Style:</label>
                            <select value={activeExperiment.restrictionMode} onChange={(e) => setActiveExperiment({...activeExperiment, restrictionMode: e.target.value})} style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '0.85rem' }}>
                                <option value="HIDE">Full Removal (Hide)</option>
                                <option value="GREY">Translucent (Grey Out & Block)</option>
                            </select>
                        </div>
                    </div>

                    {/* Individual Category Visibility Toggle */}
                    <div style={{ 
                        marginTop: '20px', 
                        padding: '15px', 
                        backgroundColor: activeExperiment.showCategories ? '#f8f9fa' : '#f0f0f0',
                        borderRadius: '8px',
                        border: '1px dashed #ccc',
                        opacity: activeExperiment.showCategories ? 1 : 0.6,
                        pointerEvents: activeExperiment.showCategories ? 'auto' : 'none'
                    }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '0.85rem', fontWeight: 'bold', color: '#555' }}>
                            {activeExperiment.showCategories 
                                ? "Uncheck specific categories to DISABLE them in the Participant Navbar:" 
                                : "Categories are currently DISABLED overall (Enable 'CategoriesOn' to modify)."}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                            {CATEGORIES.map(cat => (
                                <label key={cat.label} style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <input 
                                        type="checkbox" 
                                        checked={!hiddenCategories.includes(cat.label)} 
                                        onChange={() => toggleCategoryEnabled(cat.label)}
                                    />
                                    {cat.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </section>

                {/* BLUE FOLDER DESIGN */}
                {!loading && (
                    <div className="boxed-data-container">
                        {Object.entries(groupedData.experiments).reverse().map(([id, group]) => {
                            if (filterExpId !== 'ALL' && filterExpId !== id) return null;
                            return (
                                <div key={id} className="experiment-group-box" style={{ marginBottom: '30px' }}>
                                    <div className="exp-header-tab">
                                        <span>Study {id}: {group.name}</span>
                                        <button className="tab-dl-btn" onClick={() => downloadCSV(group.sessions)}>Download Study CSV</button>
                                    </div>
                                    <div className="exp-box-content">
                                        {group.sessions.length > 0 ? (
                                            group.sessions.map(renderSessionRow)
                                        ) : (
                                            <p style={{ padding: '20px', color: '#999' }}>No participants joined yet.</p>
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        {(filterExpId === 'ALL' || filterExpId === '1') && (
                            <div className="general-data-box" style={{ marginBottom: '30px' }}>
                                <h3 className="general-title">General Browsing / Control Data</h3>
                                <div className="general-content">
                                    {groupedData.general.map(renderSessionRow)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
};

export default Data;